import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { Users, DollarSign, Edit3, ArrowUpRight, CheckCircle, XCircle, Search, Shield, RefreshCw } from 'lucide-react';

const InvestmentAdmin = () => {
    const [activeTab, setActiveTab] = useState('access');
    const [isLoading, setIsLoading] = useState(false);

    // --- TAB 1: ACCESS MANAGEMENT STATE ---
    const [users, setUsers] = useState([]);
    const [accessData, setAccessData] = useState({});
    const [searchQuery, setSearchQuery] = useState('');

    // --- TAB 2: PROFIT DISTRIBUTION STATE ---
    const [profitPercentage, setProfitPercentage] = useState('');
    const [periodStart, setPeriodStart] = useState('');
    const [periodEnd, setPeriodEnd] = useState('');
    const [targetMonth, setTargetMonth] = useState('');
    const [targetYear, setTargetYear] = useState(new Date().getFullYear().toString());
    const [isConfirmingProfit, setIsConfirmingProfit] = useState(false);
    const [isDistributing, setIsDistributing] = useState(false);

    useEffect(() => {
        if (activeTab === 'access') {
            fetchUsersAndAccess();
        }
    }, [activeTab]);

    // --- TAB 3: TRANCHES & OVERRIDES STATE ---
    const [metricsUsers, setMetricsUsers] = useState([]);
    const [selectedOverrideUser, setSelectedOverrideUser] = useState(null);
    const [isOverrideModalOpen, setIsOverrideModalOpen] = useState(false);
    const [overrideData, setOverrideData] = useState({ total_invested: 0, ytd_return: 0 });
    const [newTranche, setNewTranche] = useState({ amount: '' });

    // --- TAB 4: WITHDRAWALS STATE ---
    const [pendingWithdrawals, setPendingWithdrawals] = useState([]);

    // --- TAB 5: TRADES STATE ---
    const [trades, setTrades] = useState([]);
    const [newTrade, setNewTrade] = useState({ asset: '', direction: 'BUY', entry_price: '', close_price: '', result: 'PENDING' });

    useEffect(() => {
        if (activeTab === 'access') fetchUsersAndAccess();
        if (activeTab === 'overrides') fetchMetricsUsers();
        if (activeTab === 'withdrawals') fetchPendingWithdrawals();
        if (activeTab === 'trades') fetchTrades();
    }, [activeTab]);

    const fetchTrades = async () => {
        setIsLoading(true);
        try {
            const { data, error } = await supabase.from('investment_hub_trades').select('*').order('created_at', { ascending: false });
            if (error) throw error;
            setTrades(data || []);
        } catch (err) {
            console.error("Failed to load trades:", err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleAddTrade = async () => {
        if (!newTrade.asset || !newTrade.entry_price) {
            alert('Asset and Entry Price are required.');
            return;
        }
        try {
            const { error } = await supabase.from('investment_hub_trades').insert({
                asset: newTrade.asset,
                direction: newTrade.direction,
                entry_price: parseFloat(newTrade.entry_price),
                close_price: newTrade.close_price ? parseFloat(newTrade.close_price) : null,
                result: newTrade.result
            });
            if (error) throw error;
            alert('Trade added successfully.');
            setNewTrade({ asset: '', direction: 'BUY', entry_price: '', close_price: '', result: 'PENDING' });
            fetchTrades();
        } catch (err) {
            alert('Failed to add trade: ' + err.message);
        }
    };

    const fetchPendingWithdrawals = async () => {
        setIsLoading(true);
        try {
            const [{ data, error }, { unifiedList }] = await Promise.all([
                supabase
                    .from('investment_hub_ledger')
                    .select('*, profiles(full_name, email)')
                    .eq('type', 'withdraw_request')
                    .eq('status', 'pending')
                    .order('created_at', { ascending: false }),
                buildUnifiedClientsAndAccess()
            ]);

            if (error) throw error;
            const userByUid = {};
            (unifiedList || []).forEach(u => {
                if (u.user_id) userByUid[String(u.user_id)] = u;
            });

            const enriched = (data || []).map(row => {
                if (row.profiles && (row.profiles.full_name || row.profiles.email)) return row;
                const matched = userByUid[String(row.user_id)];
                return {
                    ...row,
                    profiles: matched
                        ? { full_name: matched.full_name, email: matched.email }
                        : (row.profiles || { full_name: 'Unknown User', email: '' })
                };
            });
            setPendingWithdrawals(enriched);
        } catch (err) {
            console.error("Failed to fetch withdrawals:", err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleApproveWithdrawal = async (id) => {
        try {
            const { error } = await supabase
                .from('investment_hub_ledger')
                .update({ status: 'completed' })
                .eq('id', id);
            
            if (error) throw error;
            setPendingWithdrawals(prev => prev.filter(w => w.id !== id));
            alert('Withdrawal approved and marked as completed.');
        } catch (err) {
            alert('Failed to approve withdrawal: ' + err.message);
        }
    };

    const handleRejectWithdrawal = async (id, amount, userId) => {
        try {
            // 1. Mark ledger as failed
            const { error: ledgerErr } = await supabase
                .from('investment_hub_ledger')
                .update({ status: 'failed' })
                .eq('id', id);
            if (ledgerErr) throw ledgerErr;

            // 2. Refund the available_profit
            // We need to fetch current first
            const { data: metrics } = await supabase.from('investment_hub_metrics').select('available_profit').eq('user_id', userId).single();
            if (metrics) {
                await supabase
                    .from('investment_hub_metrics')
                    .update({ available_profit: parseFloat(metrics.available_profit) + parseFloat(amount) })
                    .eq('user_id', userId);
            }

            setPendingWithdrawals(prev => prev.filter(w => w.id !== id));
            alert('Withdrawal rejected and funds refunded to available_profit.');
        } catch (err) {
            alert('Failed to reject withdrawal: ' + err.message);
        }
    };

    const handleDistributeProfit = async () => {
        setIsDistributing(true);
        try {
            // 1. Call the new RPC for Prorated Distribution
            const { error: rpcError } = await supabase.rpc('distribute_hub_profits', { 
                profit_percentage: parseFloat(profitPercentage),
                period_start: periodStart,
                period_end: periodEnd
            });
            
            if (rpcError) throw rpcError;
            
            // 2. Insert into global liquidity fund performance
            const { error: perfError } = await supabase.from('liquidity_fund_performance').insert({
                month: targetMonth,
                year: parseInt(targetYear),
                roi_percentage: parseFloat(profitPercentage)
            });

            if (perfError) throw perfError;
            
            alert(`Successfully distributed ${profitPercentage}% profit for ${targetMonth} ${targetYear}!`);
            setProfitPercentage('');
            setPeriodStart('');
            setPeriodEnd('');
            setTargetMonth('');
            setTargetYear(new Date().getFullYear().toString());
            setIsConfirmingProfit(false);
        } catch (err) {
            console.error("Distribution failed:", err);
            alert("Distribution failed: " + err.message);
        } finally {
            setIsDistributing(false);
        }
    };

    const fetchAllTableRows = async (table, selectCols = '*') => {
        let all = [];
        let from = 0;
        const step = 1000;
        while (true) {
            const { data, error } = await supabase.from(table).select(selectCols).range(from, from + step - 1);
            if (error) throw error;
            if (!data || data.length === 0) break;
            all = all.concat(data);
            if (data.length < step) break;
            from += step;
        }
        return all;
    };

    const isUuid = (val) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(val || '').trim());

    const buildUnifiedClientsAndAccess = async () => {
        const [brokerAccounts, profiles, accessLogs] = await Promise.all([
            fetchAllTableRows('user_broker_accounts', 'id, user_id, email, name').catch(() => []),
            fetchAllTableRows('profiles', 'id, user_id, full_name, email').catch(() => []),
            supabase.from('investment_hub_access').select('user_id, email, has_access').then(r => r.data || [])
        ]);

        const accessByUserId = {};
        const accessByEmail = {};
        (accessLogs || []).forEach(log => {
            if (log.user_id) accessByUserId[String(log.user_id)] = log;
            const em = String(log.email || '').toLowerCase().trim();
            if (em) accessByEmail[em] = log;
        });

        const profileByEmail = {};
        const profileByUserId = {};
        (profiles || []).forEach(p => {
            const em = String(p.email || '').toLowerCase().trim();
            if (em && !profileByEmail[em]) profileByEmail[em] = p;
            if (p.user_id) profileByUserId[String(p.user_id)] = p;
        });

        const unifiedByKey = new Map();

        // 1. Primary source of truth: all clients in user_broker_accounts
        (brokerAccounts || []).forEach(acc => {
            const email = String(acc.email || '').toLowerCase().trim();
            const key = email || String(acc.user_id || acc.id || '');
            if (!key || unifiedByKey.has(key)) return;

            const prof = (email && profileByEmail[email]) || (acc.user_id && profileByUserId[String(acc.user_id)]) || null;
            const existingAccess =
                (prof?.user_id && accessByUserId[String(prof.user_id)]) ||
                (acc.user_id && accessByUserId[String(acc.user_id)]) ||
                (acc.id && accessByUserId[String(acc.id)]) ||
                (email && accessByEmail[email]) ||
                null;

            // Preserve existing investment_hub_access.user_id if already linked; otherwise prefer Auth UUID from profiles or user_broker_accounts
            const resolvedUserId =
                existingAccess?.user_id ||
                (prof?.user_id && isUuid(prof.user_id) ? prof.user_id : null) ||
                (acc.user_id && isUuid(acc.user_id) ? acc.user_id : null) ||
                (acc.id && isUuid(acc.id) ? acc.id : (prof?.user_id || acc.user_id || acc.id));

            if (!resolvedUserId) return;

            const displayName = (prof?.full_name || acc.name || '').trim() || email || 'Unknown User';

            unifiedByKey.set(key, {
                id: prof?.id || acc.id || resolvedUserId,
                user_id: String(resolvedUserId),
                full_name: displayName,
                email: email || prof?.email || ''
            });
        });

        // 2. Include any remaining profiles not already in user_broker_accounts so zero existing records are lost
        (profiles || []).forEach(prof => {
            const email = String(prof.email || '').toLowerCase().trim();
            const key = email || String(prof.user_id || prof.id || '');
            if (!key || unifiedByKey.has(key)) return;
            const existingByUid = Array.from(unifiedByKey.values()).some(u => String(u.user_id) === String(prof.user_id));
            if (existingByUid) return;

            const existingAccess =
                (prof.user_id && accessByUserId[String(prof.user_id)]) ||
                (email && accessByEmail[email]) ||
                null;
            const resolvedUserId = existingAccess?.user_id || prof.user_id || prof.id;
            if (!resolvedUserId) return;

            unifiedByKey.set(key, {
                id: prof.id || resolvedUserId,
                user_id: String(resolvedUserId),
                full_name: (prof.full_name || '').trim() || email || 'Unknown User',
                email: email
            });
        });

        const unifiedList = Array.from(unifiedByKey.values()).sort((a, b) =>
            String(a.full_name || a.email || '').localeCompare(String(b.full_name || b.email || ''))
        );

        const accessMap = {};
        unifiedList.forEach(u => {
            const em = String(u.email || '').toLowerCase().trim();
            const log = accessByUserId[u.user_id] || (em ? accessByEmail[em] : null);
            if (log) {
                accessMap[u.user_id] = Boolean(log.has_access);
            }
        });
        (accessLogs || []).forEach(log => {
            if (log.user_id && accessMap[log.user_id] === undefined) {
                accessMap[log.user_id] = Boolean(log.has_access);
            }
        });

        return { unifiedList, accessMap, accessLogs };
    };

    const fetchUsersAndAccess = async () => {
        setIsLoading(true);
        try {
            const { unifiedList, accessMap } = await buildUnifiedClientsAndAccess();
            setUsers(unifiedList);
            setAccessData(accessMap);
        } catch (err) {
            console.error("Error fetching access data:", err);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchMetricsUsers = async () => {
        setIsLoading(true);
        try {
            const { unifiedList, accessLogs } = await buildUnifiedClientsAndAccess();
            const activeLogs = (accessLogs || []).filter(log => log.has_access === true);
            const accessUserIds = activeLogs.map(log => log.user_id).filter(Boolean);

            if (accessUserIds.length === 0) {
                setMetricsUsers([]);
                setIsLoading(false);
                return;
            }

            const { data: metrics } = await supabase.from('investment_hub_metrics').select('*').in('user_id', accessUserIds);

            const userByUid = {};
            const userByEmail = {};
            unifiedList.forEach(u => {
                if (u.user_id) userByUid[String(u.user_id)] = u;
                const em = String(u.email || '').toLowerCase().trim();
                if (em) userByEmail[em] = u;
            });

            const combined = activeLogs.map(log => {
                const em = String(log.email || '').toLowerCase().trim();
                const matchedUser = userByUid[String(log.user_id)] || (em ? userByEmail[em] : null) || {
                    user_id: log.user_id,
                    full_name: log.email || 'Unknown User',
                    email: log.email || ''
                };
                const metric = (metrics || []).find(m => String(m.user_id) === String(log.user_id)) || { total_invested: 0, ytd_return: 0 };
                return {
                    ...metric,
                    ...matchedUser,
                    user_id: log.user_id
                };
            });

            setMetricsUsers(combined);
        } catch (err) {
            console.error("Failed to load metrics:", err);
        } finally {
            setIsLoading(false);
        }
    };

    const toggleAccess = async (authUserId, currentStatus) => {
        const newStatus = !currentStatus;
        setAccessData(prev => ({ ...prev, [authUserId]: newStatus }));
        try {
            const userObj = users.find(u => u.user_id === authUserId);
            const { error } = await supabase
                .from('investment_hub_access')
                .upsert({ user_id: authUserId, email: userObj?.email || '', has_access: newStatus }, { onConflict: 'user_id' });
            if (error) throw error;
        } catch (err) {
            console.error("Failed to toggle access:", err);
            setAccessData(prev => ({ ...prev, [authUserId]: currentStatus }));
        }
    };

    const handleSaveOverride = async () => {
        if (!selectedOverrideUser) return;
        try {
            const { error } = await supabase
                .from('investment_hub_metrics')
                .upsert({ 
                    user_id: selectedOverrideUser.user_id,
                    total_invested: parseFloat(overrideData.total_invested), 
                    ytd_return: parseFloat(overrideData.ytd_return)
                }, { onConflict: 'user_id' });
            if (error) throw error;

            alert('Metrics updated successfully.');
            setIsOverrideModalOpen(false);
            fetchMetricsUsers();
        } catch (err) {
            alert('Failed to update metrics: ' + err.message);
        }
    };

    const handleAddTranche = async () => {
        if (!selectedOverrideUser || !newTranche.amount) return;
        try {
            const amountToAdd = parseFloat(newTranche.amount);
            
            // 1. Insert the new tranche
            const { error: trancheErr } = await supabase
                .from('investment_hub_tranches')
                .insert({
                    user_id: selectedOverrideUser.user_id,
                    amount: amountToAdd,
                    type: 'principal'
                    // unlock_date and status are now handled automatically by our PostgreSQL trigger
                });
            if (trancheErr) throw trancheErr;

            // 2. Automatically update the wallet metrics total
            const currentTotal = selectedOverrideUser.total_invested || 0;
            const currentYtd = selectedOverrideUser.ytd_return || 0;
            const newTotal = parseFloat(currentTotal) + amountToAdd;

            const { error: metricErr } = await supabase
                .from('investment_hub_metrics')
                .upsert({ 
                    user_id: selectedOverrideUser.user_id,
                    total_invested: newTotal,
                    ytd_return: currentYtd
                }, { onConflict: 'user_id' });
            if (metricErr) throw metricErr;

            alert(`Tranche added successfully! Total Invested is now $${newTotal.toLocaleString()}.`);
            setNewTranche({ amount: '' });
            fetchMetricsUsers(); // Refresh the list
        } catch (err) {
            alert('Failed to add tranche: ' + err.message);
        }
    };

    const filteredUsers = users.filter(u => 
        (u.full_name?.toLowerCase() || '').includes(searchQuery.toLowerCase()) || 
        (u.email?.toLowerCase() || '').includes(searchQuery.toLowerCase())
    );

    return (
        <div className="p-4 md:p-8 animate-fade-in text-white min-h-screen">
            <div className="max-w-7xl mx-auto space-y-6">
                {/* Header */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-slate-900 p-6 rounded-2xl border border-slate-800">
                    <div>
                        <h1 className="text-2xl font-black text-white uppercase tracking-tight flex items-center gap-3">
                            <DollarSign className="text-amber-500" size={28}/> 
                            Investment Hub Admin
                        </h1>
                        <p className="text-slate-400 mt-1">Manage access, distribute monthly profits, and process withdrawals.</p>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex space-x-2 bg-slate-900 p-2 rounded-xl border border-slate-800 overflow-x-auto no-scrollbar">
                    <button onClick={() => setActiveTab('access')} className={`px-4 py-2 rounded-lg font-bold text-sm transition-all whitespace-nowrap ${activeTab === 'access' ? 'bg-amber-500 text-slate-950' : 'text-slate-400 hover:bg-slate-800'}`}>
                        Access Management
                    </button>
                    <button onClick={() => setActiveTab('profits')} className={`px-4 py-2 rounded-lg font-bold text-sm transition-all whitespace-nowrap ${activeTab === 'profits' ? 'bg-amber-500 text-slate-950' : 'text-slate-400 hover:bg-slate-800'}`}>
                        Distribute Profits
                    </button>
                    <button onClick={() => setActiveTab('overrides')} className={`px-4 py-2 rounded-lg font-bold text-sm transition-all whitespace-nowrap ${activeTab === 'overrides' ? 'bg-amber-500 text-slate-950' : 'text-slate-400 hover:bg-slate-800'}`}>
                        Tranches & Overrides
                    </button>
                    <button onClick={() => setActiveTab('withdrawals')} className={`px-4 py-2 rounded-lg font-bold text-sm transition-all whitespace-nowrap ${activeTab === 'withdrawals' ? 'bg-amber-500 text-slate-950' : 'text-slate-400 hover:bg-slate-800'}`}>
                        Pending Withdrawals
                    </button>
                    <button onClick={() => setActiveTab('trades')} className={`px-4 py-2 rounded-lg font-bold text-sm transition-all whitespace-nowrap ${activeTab === 'trades' ? 'bg-amber-500 text-slate-950' : 'text-slate-400 hover:bg-slate-800'}`}>
                        Manual Trades
                    </button>
                </div>

                {/* Content */}
                <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6 min-h-[500px]">
                    
                    {/* TAB 1: ACCESS MANAGEMENT */}
                    {activeTab === 'access' && (
                        <div className="space-y-8 animate-fade-in">
                            {/* NEW: Active Investors Table */}
                            <div className="space-y-4">
                                <div>
                                    <h2 className="text-xl font-bold flex items-center gap-2"><Users size={20} className="text-emerald-400" /> Active Investors</h2>
                                    <p className="text-sm text-slate-400">Users who currently have access to the Investment Hub.</p>
                                </div>
                                {isLoading ? (
                                    <div className="flex justify-center p-6 text-slate-500"><RefreshCw className="animate-spin" /></div>
                                ) : (
                                    <div className="overflow-x-auto rounded-xl border border-emerald-500/30 bg-emerald-500/5">
                                        <table className="w-full text-left border-collapse">
                                            <thead>
                                                <tr className="bg-emerald-950/50 border-b border-emerald-500/20">
                                                    <th className="p-4 text-xs font-semibold text-emerald-400 uppercase">User</th>
                                                    <th className="p-4 text-xs font-semibold text-emerald-400 uppercase text-right">Revoke Access</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-emerald-500/10">
                                                {users.filter(u => accessData[u.user_id]).map(user => (
                                                    <tr key={user.id} className="hover:bg-emerald-500/10 transition-colors">
                                                        <td className="p-4">
                                                            <div className="font-bold text-emerald-100">{user.full_name || 'Unknown User'}</div>
                                                            <div className="text-xs text-emerald-200/70">{user.email}</div>
                                                        </td>
                                                        <td className="p-4 text-right">
                                                            <button 
                                                                onClick={() => toggleAccess(user.user_id, true)}
                                                                className="bg-red-500/10 text-red-400 hover:bg-red-500 hover:text-white px-3 py-1 rounded-lg text-xs font-bold transition-colors border border-red-500/20"
                                                            >
                                                                Revoke
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                                {users.filter(u => accessData[u.user_id]).length === 0 && (
                                                    <tr><td colSpan="2" className="p-8 text-center text-emerald-500/50">No users currently have access.</td></tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>

                            <hr className="border-slate-800" />

                            <div className="flex flex-col md:flex-row justify-between gap-4">
                                <div>
                                    <h2 className="text-xl font-bold flex items-center gap-2"><Shield size={20} className="text-blue-400" /> Hub Access Gatekeeper</h2>
                                    <p className="text-sm text-slate-400">Toggle which users have access to view the Investment Hub.</p>
                                </div>
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                                    <input 
                                        type="text" 
                                        placeholder="Search users..." 
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="bg-slate-800 border border-slate-700 rounded-lg pl-10 pr-4 py-2 text-sm text-white focus:outline-none focus:border-blue-500 w-full md:w-64"
                                    />
                                </div>
                            </div>

                            {isLoading ? (
                                <div className="flex justify-center p-12 text-slate-500"><RefreshCw className="animate-spin" /></div>
                            ) : (
                                <div className="overflow-x-auto rounded-xl border border-slate-800">
                                    <table className="w-full text-left border-collapse">
                                        <thead>
                                            <tr className="bg-slate-950 border-b border-slate-800">
                                                <th className="p-4 text-xs font-semibold text-slate-400 uppercase">User</th>
                                                <th className="p-4 text-xs font-semibold text-slate-400 uppercase text-center">Hub Access</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-800">
                                            {filteredUsers.map(user => {
                                                const hasAccess = accessData[user.user_id] || false;
                                                return (
                                                    <tr key={user.id} className="hover:bg-slate-800/50 transition-colors">
                                                        <td className="p-4">
                                                            <div className="font-medium text-slate-200">{user.full_name || 'Unknown User'}</div>
                                                            <div className="text-xs text-slate-500">{user.email}</div>
                                                        </td>
                                                        <td className="p-4 text-center">
                                                            <button 
                                                                onClick={() => toggleAccess(user.user_id, hasAccess)}
                                                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${hasAccess ? 'bg-emerald-500' : 'bg-slate-700'}`}
                                                            >
                                                                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${hasAccess ? 'translate-x-6' : 'translate-x-1'}`} />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                            {filteredUsers.length === 0 && (
                                                <tr><td colSpan="2" className="p-8 text-center text-slate-500">No users found.</td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {/* TAB 2: PROFIT DISTRIBUTION */}
                    {activeTab === 'profits' && (
                        <div className="space-y-6 animate-fade-in max-w-2xl">
                            <div>
                                <h2 className="text-xl font-bold flex items-center gap-2"><DollarSign size={20} className="text-amber-400" /> Monthly Profit Distribution</h2>
                                <p className="text-sm text-slate-400 mt-1">
                                    Distribute a global percentage return to all active investment wallets. 
                                    This triggers a secure backend atomic transaction that logs the distribution in the ledger.
                                </p>
                            </div>

                            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 space-y-6">
                                <div>
                                    <label className="block text-sm font-bold text-slate-300 mb-2">Percentage Return (%)</label>
                                    <div className="relative">
                                        <input 
                                            type="number" 
                                            step="0.01"
                                            value={profitPercentage}
                                            onChange={(e) => setProfitPercentage(e.target.value)}
                                            placeholder="e.g. 2.50"
                                            className="w-full bg-slate-900 border border-slate-700 rounded-lg pl-4 pr-10 py-3 text-lg font-bold text-white focus:outline-none focus:border-amber-500 transition-colors"
                                        />
                                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 font-bold">%</span>
                                    </div>
                                    <p className="text-xs text-slate-500 mt-2">Enter the exact percentage to distribute (e.g., 2.5 for 2.5%).</p>
                                </div>
                                
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-bold text-slate-300 mb-2">Target Month</label>
                                        <select 
                                            value={targetMonth}
                                            onChange={(e) => setTargetMonth(e.target.value)}
                                            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-amber-500"
                                        >
                                            <option value="" disabled>Select Month...</option>
                                            <option value="January">January</option>
                                            <option value="February">February</option>
                                            <option value="March">March</option>
                                            <option value="April">April</option>
                                            <option value="May">May</option>
                                            <option value="June">June</option>
                                            <option value="July">July</option>
                                            <option value="August">August</option>
                                            <option value="September">September</option>
                                            <option value="October">October</option>
                                            <option value="November">November</option>
                                            <option value="December">December</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold text-slate-300 mb-2">Target Year</label>
                                        <input 
                                            type="number" 
                                            value={targetYear}
                                            onChange={(e) => setTargetYear(e.target.value)}
                                            placeholder="e.g. 2026"
                                            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-amber-500"
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-bold text-slate-300 mb-2">Period Start Date</label>
                                        <input 
                                            type="date" 
                                            value={periodStart}
                                            onChange={(e) => setPeriodStart(e.target.value)}
                                            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-amber-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold text-slate-300 mb-2">Period End Date</label>
                                        <input 
                                            type="date" 
                                            value={periodEnd}
                                            onChange={(e) => setPeriodEnd(e.target.value)}
                                            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-amber-500"
                                        />
                                    </div>
                                </div>

                                <button 
                                    onClick={() => setIsConfirmingProfit(true)}
                                    disabled={!profitPercentage || !periodStart || !periodEnd || !targetMonth || !targetYear || isDistributing}
                                    className="w-full bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold py-3 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                                >
                                    {isDistributing ? <RefreshCw className="animate-spin" size={20} /> : <CheckCircle size={20} />}
                                    {isDistributing ? 'Distributing...' : 'Distribute Profits Now'}
                                </button>
                            </div>

                            {/* Confirmation Modal Inline */}
                            {isConfirmingProfit && (
                                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4">
                                    <div className="bg-slate-900 border border-slate-700 rounded-2xl max-w-md w-full p-6 shadow-2xl animate-fade-in">
                                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                                            <Shield className="text-red-500" /> Confirm Distribution
                                        </h3>
                                        <p className="text-slate-300 mt-4">
                                            Are you absolutely sure you want to distribute a <strong className="text-amber-500">{profitPercentage}%</strong> return for <strong className="text-emerald-400">{targetMonth} {targetYear}</strong>?
                                        </p>
                                        <p className="text-sm text-slate-500 mt-2">
                                            This action will calculate prorated returns based on the period ({periodStart} to {periodEnd}), instantly update all active wallets, and update global chart metrics. This cannot be undone automatically.
                                        </p>
                                        
                                        <div className="flex justify-end gap-3 mt-8">
                                            <button 
                                                onClick={() => setIsConfirmingProfit(false)}
                                                className="px-4 py-2 rounded-lg font-bold text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
                                            >
                                                Cancel
                                            </button>
                                            <button 
                                                onClick={handleDistributeProfit}
                                                className="px-4 py-2 rounded-lg font-bold bg-red-500 hover:bg-red-600 text-white transition-colors flex items-center gap-2"
                                            >
                                                Yes, Distribute {profitPercentage}%
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* TAB 3: TRANCHES & OVERRIDES */}
                    {activeTab === 'overrides' && (
                        <div className="space-y-6 animate-fade-in">
                            <div>
                                <h2 className="text-xl font-bold flex items-center gap-2"><Edit3 size={20} className="text-blue-400" /> Manual Tranches & Metrics Overrides</h2>
                                <p className="text-sm text-slate-400 mt-1">Select an active investor to inject tranches or manually adjust their wallet metrics.</p>
                            </div>

                            {isLoading ? (
                                <div className="flex justify-center p-12 text-slate-500"><RefreshCw className="animate-spin" /></div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {metricsUsers.map(user => (
                                        <div 
                                            key={user.user_id} 
                                            onClick={() => {
                                                setSelectedOverrideUser(user);
                                                setOverrideData({ total_invested: user.total_invested, ytd_return: user.ytd_return });
                                                setIsOverrideModalOpen(true);
                                            }}
                                            className="bg-slate-800/50 border border-slate-700 hover:border-blue-500 p-4 rounded-xl cursor-pointer transition-colors"
                                        >
                                            <div className="font-bold text-slate-200">{user.full_name || 'Unknown User'}</div>
                                            <div className="text-xs text-slate-500">{user.email || user.user_id}</div>
                                            <div className="mt-3 flex justify-between text-sm">
                                                <span className="text-slate-400">Total Invested:</span>
                                                <span className="font-bold text-amber-500">${Number(user.total_invested).toLocaleString()}</span>
                                            </div>
                                            <div className="mt-1 flex justify-between text-sm">
                                                <span className="text-slate-400">YTD Return:</span>
                                                <span className="font-bold text-emerald-400">{Number(user.ytd_return).toFixed(2)}%</span>
                                            </div>
                                        </div>
                                    ))}
                                    {metricsUsers.length === 0 && (
                                        <div className="col-span-full p-8 text-center text-slate-500 bg-slate-800/30 rounded-xl border border-slate-800 border-dashed">
                                            No active investment wallets found.
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Override Modal */}
                            {isOverrideModalOpen && selectedOverrideUser && (
                                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 overflow-y-auto">
                                    <div className="bg-slate-900 border border-slate-700 rounded-2xl max-w-2xl w-full p-6 shadow-2xl animate-fade-in my-8">
                                        <div className="flex justify-between items-start mb-6">
                                            <div>
                                                <h3 className="text-2xl font-bold text-white">Manage {selectedOverrideUser.full_name}</h3>
                                                <p className="text-sm text-slate-500">{selectedOverrideUser.email}</p>
                                            </div>
                                            <button onClick={() => setIsOverrideModalOpen(false)} className="text-slate-500 hover:text-white p-1">
                                                <XCircle size={24} />
                                            </button>
                                        </div>

                                        <div className="space-y-8">
                                            {/* Metrics Override */}
                                            <div className="bg-slate-800/50 p-5 rounded-xl border border-slate-700">
                                                <h4 className="font-bold text-slate-300 mb-4 flex items-center gap-2"><Edit3 size={16} /> Override Wallet Metrics</h4>
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                    <div>
                                                        <label className="block text-xs font-bold text-slate-400 mb-1">Total Invested ($)</label>
                                                        <input 
                                                            type="number" 
                                                            value={overrideData.total_invested}
                                                            onChange={(e) => setOverrideData({...overrideData, total_invested: e.target.value})}
                                                            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-blue-500"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-bold text-slate-400 mb-1">YTD Return (%)</label>
                                                        <input 
                                                            type="number" 
                                                            value={overrideData.ytd_return}
                                                            onChange={(e) => setOverrideData({...overrideData, ytd_return: e.target.value})}
                                                            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-blue-500"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="mt-4 flex justify-end">
                                                    <button onClick={handleSaveOverride} className="bg-blue-600 hover:bg-blue-500 text-white font-bold py-2 px-4 rounded-lg transition-colors text-sm">
                                                        Save Metrics
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="bg-slate-800/50 p-5 rounded-xl border border-slate-700">
                                                <h4 className="font-bold text-slate-300 mb-4 flex items-center gap-2"><ArrowUpRight size={16} /> Inject New Tranche (Principal)</h4>
                                                <div className="grid grid-cols-1 gap-4">
                                                    <div>
                                                        <label className="block text-xs font-bold text-slate-400 mb-1">Amount ($)</label>
                                                        <input 
                                                            type="number" 
                                                            value={newTranche.amount}
                                                            onChange={(e) => setNewTranche({...newTranche, amount: e.target.value})}
                                                            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-amber-500"
                                                        />
                                                        <p className="text-[10px] text-slate-500 mt-2">The lockup date will automatically be set to 6 months from today by the database trigger.</p>
                                                    </div>
                                                </div>
                                                <div className="mt-4 flex justify-end">
                                                    <button onClick={handleAddTranche} className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold py-2 px-4 rounded-lg transition-colors text-sm">
                                                        Inject Tranche
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* TAB 4: WITHDRAWALS */}
                    {activeTab === 'withdrawals' && (
                        <div className="space-y-6 animate-fade-in">
                            <div>
                                <h2 className="text-xl font-bold flex items-center gap-2"><ArrowUpRight size={20} className="text-emerald-400" /> Pending Withdrawals</h2>
                                <p className="text-sm text-slate-400 mt-1">Review and process user withdrawal requests from their available profit.</p>
                            </div>

                            {isLoading ? (
                                <div className="flex justify-center p-12 text-slate-500"><RefreshCw className="animate-spin" /></div>
                            ) : (
                                <div className="overflow-x-auto rounded-xl border border-slate-800">
                                    <table className="w-full text-left border-collapse">
                                        <thead>
                                            <tr className="bg-slate-950 border-b border-slate-800">
                                                <th className="p-4 text-xs font-semibold text-slate-400 uppercase">User</th>
                                                <th className="p-4 text-xs font-semibold text-slate-400 uppercase">Amount</th>
                                                <th className="p-4 text-xs font-semibold text-slate-400 uppercase">Requested</th>
                                                <th className="p-4 text-xs font-semibold text-slate-400 uppercase text-right">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-800">
                                            {pendingWithdrawals.map(req => (
                                                <tr key={req.id} className="hover:bg-slate-800/50 transition-colors">
                                                    <td className="p-4">
                                                        <div className="font-medium text-slate-200">{req.profiles?.full_name || 'Unknown User'}</div>
                                                        <div className="text-xs text-slate-500">{req.profiles?.email || req.user_id}</div>
                                                    </td>
                                                    <td className="p-4">
                                                        <span className="font-bold text-amber-500">${Number(req.amount).toLocaleString()}</span>
                                                    </td>
                                                    <td className="p-4 text-sm text-slate-400">
                                                        {new Date(req.created_at).toLocaleDateString()}
                                                    </td>
                                                    <td className="p-4 text-right space-x-2">
                                                        <button 
                                                            onClick={() => handleApproveWithdrawal(req.id)}
                                                            className="bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500 hover:text-white px-3 py-1 rounded-lg text-sm transition-colors border border-emerald-500/20"
                                                        >
                                                            Approve
                                                        </button>
                                                        <button 
                                                            onClick={() => {
                                                                if(window.confirm('Reject this withdrawal? The funds will be refunded.')) {
                                                                    handleRejectWithdrawal(req.id, req.amount, req.user_id);
                                                                }
                                                            }}
                                                            className="bg-red-500/10 text-red-400 hover:bg-red-500 hover:text-white px-3 py-1 rounded-lg text-sm transition-colors border border-red-500/20"
                                                        >
                                                            Reject
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                            {pendingWithdrawals.length === 0 && (
                                                <tr><td colSpan="4" className="p-8 text-center text-slate-500">No pending withdrawals.</td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {/* TAB 5: MANUAL TRADES */}
                    {activeTab === 'trades' && (
                        <div className="space-y-6 animate-fade-in">
                            <div>
                                <h2 className="text-xl font-bold flex items-center gap-2"><ArrowUpRight size={20} className="text-purple-400" /> Manual Trades Log</h2>
                                <p className="text-sm text-slate-400 mt-1">Insert trades the investment system has taken.</p>
                            </div>

                            <div className="bg-slate-800/50 p-6 rounded-xl border border-slate-700">
                                <h3 className="font-bold text-slate-200 mb-4">Add New Trade</h3>
                                <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
                                    <div>
                                        <label className="block text-xs font-bold text-slate-400 mb-1">Asset (e.g. GBPUSD)</label>
                                        <input type="text" value={newTrade.asset} onChange={e => setNewTrade({...newTrade, asset: e.target.value.toUpperCase()})} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white" placeholder="GBPUSD" />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-400 mb-1">Direction</label>
                                        <select value={newTrade.direction} onChange={e => setNewTrade({...newTrade, direction: e.target.value})} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white">
                                            <option value="BUY">BUY</option>
                                            <option value="SELL">SELL</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-400 mb-1">Entry Price</label>
                                        <input type="number" step="0.00001" value={newTrade.entry_price} onChange={e => setNewTrade({...newTrade, entry_price: e.target.value})} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white" placeholder="1.20000" />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-400 mb-1">Close Price</label>
                                        <input type="number" step="0.00001" value={newTrade.close_price} onChange={e => setNewTrade({...newTrade, close_price: e.target.value})} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white" placeholder="Optional" />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-400 mb-1">Result</label>
                                        <select value={newTrade.result} onChange={e => setNewTrade({...newTrade, result: e.target.value})} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white">
                                            <option value="PENDING">PENDING</option>
                                            <option value="WIN">WIN</option>
                                            <option value="LOSS">LOSS</option>
                                        </select>
                                    </div>
                                    <div className="md:col-span-5 flex justify-end mt-2">
                                        <button onClick={handleAddTrade} className="bg-purple-600 hover:bg-purple-500 text-white font-bold py-2 px-6 rounded-lg transition-colors">
                                            Add Trade
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="overflow-x-auto rounded-xl border border-slate-800">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="bg-slate-800/80 border-b border-slate-700">
                                            <th className="p-4 text-xs font-semibold text-slate-400 uppercase">Date</th>
                                            <th className="p-4 text-xs font-semibold text-slate-400 uppercase">Asset</th>
                                            <th className="p-4 text-xs font-semibold text-slate-400 uppercase">Direction</th>
                                            <th className="p-4 text-xs font-semibold text-slate-400 uppercase">Entry</th>
                                            <th className="p-4 text-xs font-semibold text-slate-400 uppercase">Close</th>
                                            <th className="p-4 text-xs font-semibold text-slate-400 uppercase">Result</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-800">
                                        {trades.map(trade => (
                                            <tr key={trade.id} className="hover:bg-slate-800/50 transition-colors">
                                                <td className="p-4 text-sm text-slate-400">{new Date(trade.created_at).toLocaleString()}</td>
                                                <td className="p-4 font-bold text-slate-200">{trade.asset}</td>
                                                <td className="p-4">
                                                    <span className={`px-2 py-1 rounded text-xs font-bold ${trade.direction === 'BUY' ? 'bg-blue-500/20 text-blue-400' : 'bg-red-500/20 text-red-400'}`}>
                                                        {trade.direction}
                                                    </span>
                                                </td>
                                                <td className="p-4 font-mono text-sm text-slate-300">{trade.entry_price}</td>
                                                <td className="p-4 font-mono text-sm text-slate-300">{trade.close_price || '-'}</td>
                                                <td className="p-4">
                                                    <span className={`px-2 py-1 rounded text-xs font-bold ${trade.result === 'WIN' ? 'bg-emerald-500/20 text-emerald-400' : trade.result === 'LOSS' ? 'bg-red-500/20 text-red-400' : 'bg-slate-500/20 text-slate-400'}`}>
                                                        {trade.result}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                        {trades.length === 0 && !isLoading && (
                                            <tr><td colSpan="6" className="p-8 text-center text-slate-500">No manual trades logged yet.</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default InvestmentAdmin;
